import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const commissionTrendData = [
  { month: "Jan", amount: 4000 },
  { month: "Feb", amount: 3000 },
  { month: "Mar", amount: 5000 },
  { month: "Apr", amount: 4500 },
  { month: "May", amount: 6000 },
  { month: "Jun", amount: 7000 },
];

const commissionByUserData = [
  { name: "Alice Johnson", value: 12000 },
  { name: "Mohamed Diallo", value: 9000 },
  { name: "Fatima Ahmed", value: 7000 },
  { name: "Other", value: 4000 },
];

export default function CommissionsPage() {
  const [commissions, setCommissions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    // Replace with API call
    setCommissions([
      { id: "COM001", user: "Alice Johnson", amount: 150, date: "2023-05-01", status: "Paid" },
      { id: "COM002", user: "Mohamed Diallo", amount: 200, date: "2023-05-05", status: "Pending" },
      { id: "COM003", user: "Fatima Ahmed", amount: 120, date: "2023-04-25", status: "Paid" },
    ]);
  }, []);

  const filteredCommissions = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return commissions.filter(
      (c) =>
        (c.id.toLowerCase().includes(lowerSearch) ||
          c.user.toLowerCase().includes(lowerSearch)) &&
        (statusFilter === "All" || c.status === statusFilter)
    );
  }, [commissions, search, statusFilter]);

  const totalPages = Math.ceil(filteredCommissions.length / perPage);

  const paginatedCommissions = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredCommissions.slice(start, start + perPage);
  }, [filteredCommissions, page, perPage]);

  const totalPaid = useMemo(() => {
    return commissions
      .filter((c) => c.status === "Paid")
      .reduce((sum, c) => sum + c.amount, 0);
  }, [commissions]);

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow md:ml-64">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
        Commissions Management
      </h2>

      {/* Summary */}
      <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900 rounded text-blue-900 dark:text-blue-100 font-semibold">
        Total Commissions Paid:{" "}
        {totalPaid.toLocaleString(undefined, {
          style: "currency",
          currency: "CAD",
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Commission Trend Line Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Commission Trend (Last 6 Months)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={commissionTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="month" stroke="#8884d8" />
              <YAxis stroke="#8884d8" />
              <Tooltip />
              <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Commission Distribution Pie Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Commission Distribution by User</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={commissionByUserData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                label
              >
                {commissionByUserData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-4">
        <input
          type="text"
          placeholder="Search by commission ID or user"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="flex-grow p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-2 sm:mb-0"
        />

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="All">All Statuses</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* Commission List Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-200 dark:border-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Commission ID
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                User
              </th>
              <th className="py-2 px-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                Amount
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Date
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedCommissions.length === 0 ? (
              <tr>
                <td
                  colSpan="5"
                  className="text-center py-6 text-gray-500 dark:text-gray-400"
                >
                  No commissions found.
                </td>
              </tr>
            ) : (
              paginatedCommissions.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <td className="py-3 px-4">{c.id}</td>
                  <td className="py-3 px-4">{c.user}</td>
                  <td className="py-3 px-4 text-right font-mono">
                    {c.amount.toLocaleString(undefined, {
                      style: "currency",
                      currency: "CAD",
                    })}
                  </td>
                  <td className="py-3 px-4">{c.date}</td>
                  <td className="py-3 px-4">{c.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between items-center text-gray-700 dark:text-gray-300">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
          className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={page === totalPages}
          className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
